import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { EnvelopeIcon } from '@heroicons/react/24/outline';
import NewsletterSignup from '../components/NewsletterSignup';
import { useScrollToTop } from '../hooks/useScrollToTop';

const ContactPage: React.FC = () => {
  // Scroll to top when page loads
  useScrollToTop();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<{
    type: 'success' | 'error' | null;
    message: string;
  }>({ type: null, message: '' });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus({ type: null, message: '' });

    try {
      // Use the correct API URL for development vs production
      const apiUrl = process.env.NODE_ENV === 'development' 
        ? 'http://localhost:5001/api/contact'
        : '/api/contact';
        
      console.log('Submitting contact form to:', apiUrl);
      
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      console.log('Response status:', response.status);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      console.log('Response data:', result);

      if (result.success) {
        setSubmitStatus({
          type: 'success',
          message: result.message
        });
        // Reset form on success
        setFormData({ name: '', email: '', subject: '', message: '' });
      } else {
        setSubmitStatus({
          type: 'error',
          message: result.message
        });
      }
    } catch (error) {
      console.error('Contact form error:', error);
      setSubmitStatus({
        type: 'error',
        message: 'Network error. Please check your connection and try again.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <>
      <Helmet>
        <title>Contact Destress Daily - Get in Touch</title>
        <meta name="description" content="Contact Destress Daily for questions, feedback, or collaboration opportunities. We'd love to hear from you!" />
        <meta name="keywords" content="contact destress daily, stress relief tips feedback, wellness questions" />
        <meta property="og:title" content="Contact Destress Daily - Get in Touch" />
        <meta property="og:description" content="Contact Destress Daily for questions, feedback, or collaboration opportunities." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://dailyhealthhacks.com/contact" />
        <link rel="canonical" href="https://dailyhealthhacks.com/contact" />
      </Helmet>

      <div className="pt-8">
        {/* Hero Section */}
        <div className="bg-gradient-to-br from-primary-50 via-white to-health-50">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-primary-gradient rounded-2xl mb-6">
                <EnvelopeIcon className="w-8 h-8 text-white" />
              </div>
              <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                Get in Touch
              </h1>
              <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
                Have questions, feedback, or want to collaborate? We'd love to hear from you!
              </p>
            </div>
          </div>
        </div>

        {/* Contact Form Section */}
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-8">
                Send us a Message
              </h2>
              
              {/* Status Messages */}
              {submitStatus.type && (
                <div className={`mb-6 p-4 rounded-lg ${
                  submitStatus.type === 'success' 
                    ? 'bg-green-100 text-green-800 border border-green-200' 
                    : 'bg-red-100 text-red-800 border border-red-200'
                }`}>
                  {submitStatus.message}
                </div>
              )}
              
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                      Name *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      disabled={isSubmitting}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                      placeholder="Your name"
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                      Email *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      disabled={isSubmitting}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                      placeholder="your.email@example.com"
                    />
                  </div>
                </div>
                
                <div>
                  <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-2">
                    Subject *
                  </label>
                  <select
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    required
                    disabled={isSubmitting}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                  >
                    <option value="">Select a subject</option>
                    <option value="general">General Inquiry</option>
                    <option value="feedback">Feedback</option>
                    <option value="collaboration">Collaboration</option>
                    <option value="content-suggestion">Content Suggestion</option>
                    <option value="technical">Technical Issue</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                
                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                    Message *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    disabled={isSubmitting}
                    rows={6}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                    placeholder="Tell us how we can help you..."
                  />
                </div>
                
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="btn-primary text-lg px-8 py-3 w-full md:w-auto disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? 'Sending...' : 'Send Message'}
                </button>
              </form>
            </div>

            {/* Contact Information */}
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-8">
                Contact Information
              </h2>
              
              <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8 mb-8">
                <div className="space-y-6">
                  <div className="flex items-start">
                    <div className="w-12 h-12 bg-primary-600 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                      <EnvelopeIcon className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">Email</h3>
                      <p className="text-gray-600 font-medium">help@destress-daily.com</p>
                      <p className="text-sm text-gray-500 mt-1">We'll get back to you within 24-48 hours</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* FAQ Section */}
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-6">
                  Frequently Asked Questions
                </h3>
                
                <div className="space-y-4">
                  <div className="bg-white border border-gray-200 rounded-xl p-6 hover:shadow-sm transition-shadow duration-200">
                    <h4 className="font-semibold text-gray-900 mb-3">
                      How often do you publish new daily tips?
                    </h4>
                    <p className="text-gray-600">
                      We publish new stress relief tips regularly to keep you updated with the latest wellness advice and mindfulness techniques.
                    </p>
                  </div>

                  <div className="bg-white border border-gray-200 rounded-xl p-6 hover:shadow-sm transition-shadow duration-200">
                    <h4 className="font-semibold text-gray-900 mb-3">
                      Can I suggest a daily tip topic?
                    </h4>
                    <p className="text-gray-600">
                      Absolutely! We love hearing from our readers. Use the contact form above to suggest topics you'd like us to cover.
                    </p>
                  </div>

                  <div className="bg-white border border-gray-200 rounded-xl p-6 hover:shadow-sm transition-shadow duration-200">
                    <h4 className="font-semibold text-gray-900 mb-3">
                      How can I collaborate with Destress Daily?
                    </h4>
                    <p className="text-gray-600">
                      We're always open to collaborations with stress management professionals, wellness experts, and brands that align with our stress relief mission.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Newsletter Signup */}
      <NewsletterSignup variant="hero" />
    </>
  );
};

export default ContactPage; 